/**
 * Presentational cards for a Canvas SIS-import result. Shared by the Upload
 * detail page and the SIS Bundle detail page so the "what Canvas did" story
 * looks identical on both surfaces.
 *
 * Each card takes plain props (not an upload/bundle row) and returns null when
 * there's nothing to show, so callers can render them unconditionally and let
 * partial/missing Canvas data degrade gracefully.
 */

import type { ReactNode } from "react";
import { formatTime } from "../lib/format";
import { humanizeCountKey, humanizeProcessingError } from "../lib/canvasResult";

/**
 * "What Canvas changed" — Canvas's per-resource counts as a kv-table, with an
 * optional error message and a short note in the same card.
 *
 * Renders null unless there's something worth showing. `forceRender` lets a
 * caller surface the card (e.g. a failure with no counts yet) even when empty.
 */
export function CanvasResultCard({
  title = "Result",
  counts,
  errorMessage,
  note,
  forceRender,
}: {
  title?: string;
  counts?: Record<string, number>;
  errorMessage?: string;
  note?: ReactNode;
  forceRender?: boolean;
}) {
  const hasCounts = !!counts && Object.keys(counts).length > 0;
  if (!hasCounts && !errorMessage && !note && !forceRender) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>{title}</h2>
        {note && <span className="muted small">{note}</span>}
      </div>

      {errorMessage && (
        <div className="error" style={{ marginBottom: 12 }}>
          {errorMessage}
        </div>
      )}

      {hasCounts && (
        <div className="card-sub">
          <div className="card-sub-head">What Canvas changed</div>
          <table className="kv-table">
            <tbody>
              {Object.entries(counts!).map(([k, v]) => (
                <tr key={k}>
                  <td className="kv-key">{humanizeCountKey(k)}</td>
                  <td className="kv-val mono">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * "Messages from Canvas" — processing error/warning counts plus humanized
 * samples of the actual error AND warning lines. Renders null when Canvas
 * reported neither. `sample` is the error sample (kept as the prop name for
 * back-compat with the Upload detail); `warningsSample` is the warning sample.
 */
export function CanvasMessagesCard({
  warnings = 0,
  errors = 0,
  sample = [],
  warningsSample = [],
}: {
  warnings?: number;
  errors?: number;
  /** Sample of Canvas `processing_errors` lines. */
  sample?: string[];
  /** Sample of Canvas `processing_warnings` lines. */
  warningsSample?: string[];
}) {
  if (warnings === 0 && errors === 0) return null;
  const hasErr = sample.length > 0;
  const hasWarn = warningsSample.length > 0;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Messages from Canvas</h2>
        <div className="muted small">
          {errors > 0 && <>Errors: {errors}. </>}
          {warnings > 0 && <>Warnings: {warnings}. </>}
        </div>
      </div>

      {!hasErr && !hasWarn ? (
        <div className="muted small">
          Canvas reported {errors} error{errors === 1 ? "" : "s"} and {warnings}{" "}
          warning{warnings === 1 ? "" : "s"}, but didn't include specific
          message text.
        </div>
      ) : (
        <>
          {hasErr && <MessageList kind="error" lines={sample} />}
          {hasWarn && <MessageList kind="warning" lines={warningsSample} />}
        </>
      )}
    </div>
  );
}

/** A humanized list of Canvas processing messages (errors or warnings) plus a
 *  raw-text disclosure. */
function MessageList({
  kind,
  lines,
}: {
  kind: "error" | "warning";
  lines: string[];
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="muted small" style={{ marginBottom: 8 }}>
        First {lines.length} {kind}
        {lines.length === 1 ? "" : "s"} from Canvas:
      </div>
      <ul className="error-list">
        {lines.map((line, i) => (
          <li key={i} className="error-list-item">
            {humanizeProcessingError(line)}
          </li>
        ))}
      </ul>
      <details className="raw-details">
        <summary>Show technical details</summary>
        <pre className="raw-block">{lines.join("\n")}</pre>
      </details>
    </div>
  );
}

/**
 * "Canvas status checks" — the bounded poll-sample audit (timestamped
 * workflow_state transitions). Renders null when there are no samples.
 */
export function CanvasStatusChecksCard({
  samples,
}: {
  samples?: { at: string; workflowState: string }[];
}) {
  if (!samples || samples.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Canvas status checks</h2>
        <div className="muted small">
          {samples.length} check{samples.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="poll-history">
        {samples.map((s, i) => (
          <div key={i} className="poll-row">
            <span className="poll-time mono">{formatTime(s.at)}</span>
            <span className="poll-state mono">{s.workflowState}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
